import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import { trimTextOnBlur } from './utils/formHelpers';
import { outlinedFieldProps } from './utils/fieldStyles';

const COPY_FROM_DAY_TOOLTIP =
    'Copy title and line items from an earlier day into this day. Existing content on this day will be replaced.';

/**
 * Itinerary daily schedule: day title/description, Copy from day, line items.
 */
const DaySchedule = ({
    duration,
    itineraryDays,
    onDayChange,
    onAddLineItem,
    onLineItemChange,
    onRemoveLineItem,
    stepConfig,
    errors,
    renderRows,
}) => {
    const days = itineraryDays || [];
    const showDayLabels = days.length > 1;
    const lineItemFields = stepConfig.lineItemFields || {};
    const lineItemRows = [
        ['item_title', 'trip_component_map', 'location'],
        ['sequence'],
        ['day_line_item_description'],
    ];

    const [copyMenuAnchor, setCopyMenuAnchor] = useState(null);
    const [copyTargetDayIndex, setCopyTargetDayIndex] = useState(null);

    const openCopyMenu = (event, dayIndex) => {
        setCopyMenuAnchor(event.currentTarget);
        setCopyTargetDayIndex(dayIndex);
    };

    const closeCopyMenu = () => {
        setCopyMenuAnchor(null);
        setCopyTargetDayIndex(null);
    };

    const handleCopyFromDay = (sourceIndex) => {
        if (copyTargetDayIndex == null) return;
        if (Number.isNaN(sourceIndex) || sourceIndex === copyTargetDayIndex) {
            closeCopyMenu();
            return;
        }
        const source = days[sourceIndex];
        if (!source) {
            closeCopyMenu();
            return;
        }
        onDayChange(copyTargetDayIndex, 'day_title', source.day_title || '');
        const clonedLineItems = (source.line_items || []).map((item) => {
            const { id: _id, ...rest } = item || {};
            return { ...rest };
        });
        onDayChange(copyTargetDayIndex, 'line_items', clonedLineItems);
        closeCopyMenu();
    };

    if (days.length === 0) return null;

    const copySourceDays =
        copyTargetDayIndex == null
            ? []
            : days
                .map((d, i) => ({ day: d, index: i }))
                .filter(({ index }) => index < copyTargetDayIndex);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <MDTypography variant="subtitle2" fontWeight="medium" color="text">
                Daily Schedule ({duration} {duration === 1 ? 'day' : 'days'})
            </MDTypography>

            {days.map((day, dayIndex) => {
                const showCopyButton = dayIndex > 0;
                const lineItems = day.line_items || [];

                return (
                    <Box
                        key={dayIndex}
                        className="day-item"
                        sx={{
                            border: '1px solid',
                            borderColor: 'grey.400',
                            borderRadius: 0.5,
                            overflow: 'hidden',
                            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                            '&:hover': {
                                borderColor: 'grey.600',
                                boxShadow: '0 2px 10px rgba(17, 42, 82, 0.06)',
                            },
                        }}
                    >
                        <MDBox
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{
                                px: 2,
                                py: 1,
                                bgcolor: 'grey.200',
                                borderBottom: '1px solid',
                                borderColor: 'grey.400',
                            }}
                        >
                            <MDBox display="flex" alignItems="center" gap={1}>
                                <MDBox
                                    sx={{
                                        width: 22,
                                        height: 22,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: '#D6E3FF',
                                        flexShrink: 0,
                                    }}
                                >
                                    <MDTypography
                                        variant="caption"
                                        fontWeight="medium"
                                        sx={{ color: '#1565C8', fontSize: '0.7rem' }}
                                    >
                                        {day.day_number}
                                    </MDTypography>
                                </MDBox>
                                <MDTypography variant="subtitle2" fontWeight="medium">
                                    Day {day.day_number}
                                </MDTypography>
                            </MDBox>

                            {showCopyButton && (
                                <Tooltip title={COPY_FROM_DAY_TOOLTIP} arrow>
                                    <span>
                                        <MDButton
                                            variant="outlined"
                                            color="info"
                                            size="small"
                                            startIcon={<ContentCopyIcon fontSize="small" />}
                                            onClick={(e) => openCopyMenu(e, dayIndex)}
                                            sx={{
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                borderRadius: 1.5,
                                                px: 1.5,
                                                py: 0.5,
                                                minHeight: 32,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            Copy from day
                                        </MDButton>
                                    </span>
                                </Tooltip>
                            )}
                        </MDBox>

                        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Box data-trip-field={`days.${dayIndex}.day_title`}>
                                        <TextField
                                            {...outlinedFieldProps}
                                            label="Day Title"
                                            required
                                            value={day.day_title || ''}
                                            onChange={(e) => onDayChange(dayIndex, 'day_title', e.target.value)}
                                            onBlur={(e) => trimTextOnBlur(e.target.value, (next) => onDayChange(dayIndex, 'day_title', next))}
                                            error={errors.some((e) => e.key === `days.${dayIndex}.day_title`)}
                                            helperText={errors.find((e) => e.key === `days.${dayIndex}.day_title`)?.message || ''}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {lineItems.map((lineItem, itemIndex) => {
                                    const itemPrefix = `days.${dayIndex}.line_items.${itemIndex}`;
                                    return (
                                        <MDBox
                                            key={itemIndex}
                                            sx={{
                                                p: 2,
                                                borderLeft: '3px solid',
                                                borderColor: '#1565C8',
                                                borderRadius: 1,
                                                bgcolor: 'rgba(21, 101, 200, 0.04)',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 2,
                                            }}
                                        >
                                            <MDBox display="flex" justifyContent="space-between" alignItems="center">
                                                <MDBox
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        alignSelf: 'flex-start',
                                                        px: 1.5,
                                                        py: 0.5,
                                                        borderRadius: 5,
                                                        bgcolor: '#D6E3FF',
                                                    }}
                                                >
                                                    <MDTypography
                                                        variant="caption"
                                                        fontWeight="medium"
                                                        sx={{ color: '#1565C8', fontSize: '0.7rem', lineHeight: 1 }}
                                                    >
                                                        Line Item {itemIndex + 1}
                                                    </MDTypography>
                                                </MDBox>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => onRemoveLineItem(dayIndex, itemIndex)}
                                                    sx={{
                                                        color: 'text.secondary',
                                                        '&:hover': { color: 'error.main', bgcolor: 'rgba(186, 26, 26, 0.08)' },
                                                    }}
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </MDBox>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                {renderRows(
                                                    lineItemRows,
                                                    lineItemFields,
                                                    lineItem,
                                                    (key, val) => onLineItemChange(dayIndex, itemIndex, key, val),
                                                    itemPrefix,
                                                    {
                                                        attachContext: {
                                                            dayIndex,
                                                            itemIndex,
                                                            dayNumber: day.day_number,
                                                        },
                                                    }
                                                )}
                                            </Box>
                                        </MDBox>
                                    );
                                })}

                                <Box
                                    onClick={() => onAddLineItem(dayIndex)}
                                    sx={{
                                        borderRadius: 2,
                                        p: 1.25,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 0.75,
                                        cursor: 'pointer',
                                        color: '#1565C8',
                                        border: '1px dashed',
                                        borderColor: 'grey.500',
                                        transition: 'background-color 0.15s ease, border-color 0.15s ease',
                                        '&:hover': {
                                            bgcolor: 'rgba(21, 101, 200, 0.05)',
                                            borderColor: '#1565C8',
                                        },
                                    }}
                                >
                                    <AddIcon fontSize="small" />
                                    <MDTypography variant="button" fontWeight="medium" sx={{ color: 'inherit' }}>
                                        Add Line Item
                                    </MDTypography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                );
            })}

            <Menu
                anchorEl={copyMenuAnchor}
                open={Boolean(copyMenuAnchor)}
                onClose={closeCopyMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                    sx: {
                        mt: 0.5,
                        minWidth: 220,
                        borderRadius: 2,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    },
                }}
            >
                <MenuItem disabled sx={{ opacity: 1, fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                    Copy from…
                </MenuItem>
                {copySourceDays.length === 0 ? (
                    <MenuItem disabled>No earlier days available</MenuItem>
                ) : (
                    copySourceDays.map(({ day: d, index: i }) => (
                        <MenuItem key={d.day_number || i} onClick={() => handleCopyFromDay(i)}>
                            Day {d.day_number}{d.day_title ? `: ${d.day_title}` : ''}
                        </MenuItem>
                    ))
                )}
            </Menu>
        </Box>
    );
};

export default DaySchedule;
